import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import dayjs from "dayjs";
import {
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import { useDispatch } from "react-redux";
import { getOrderDetailsRequest } from "../../../../redux/orders/slice";
import { setAppointmentDateRequest } from "../../../../utils/APICalls";
import Loading from "../../../../components/loading/loading";
import ErrorNotification from "../../../../components/error-notification/error-notification";

const styles = {
  icon: {
    position: "absolute",
    right: 12,
    top: 12,
    color: "rgba(0, 0, 0, 0.54)",
  },
  content: {
    width: 500,
    mt: -2,
  },
  save: {
    ml: 2,
    mr: 2,
  },
};

const validationSchema = Yup.object({
  appointmentDate: Yup.string().required("This field can't be empty"),
  reason: Yup.string(),
});

export default function AppointmentDateDialog({
  orderId,
  isEdit,
  showDialog,
  closeDialog,
}: any) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik({
    initialValues: {
      appointmentDate: "",
      reason: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        appointmentDate: formik.values.appointmentDate,
        appointmentRescheduleReason: formik.values.reason,
      };
      setAppointmentDate(payload);
    },
  });

  const setAppointmentDate = async (payload: any) => {
    setLoading(true);
    const response = await setAppointmentDateRequest(orderId, payload);
    setLoading(false);
    if (response?.status === 204) {
      dispatch(getOrderDetailsRequest({ id: orderId }));
      closeDialog();
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <Dialog
      open={showDialog}
      onClose={closeDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ fontWeight: "510" }}>
        {isEdit
          ? "Reschedule Appointment/Completion Date"
          : "Set Appointment/Completion Date"}
      </DialogTitle>
      <IconButton aria-label="close" onClick={closeDialog} sx={styles.icon}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={styles.content}>
        <form onSubmit={formik.handleSubmit}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ width: "100%" }}>
              <DatePicker
                label="Appointment Date"
                open={open}
                onClose={() => setOpen(false)}
                value={
                  formik.values.appointmentDate
                    ? dayjs(formik.values.appointmentDate)
                    : null
                }
                onChange={(date) => {
                  formik.setFieldValue(
                    "appointmentDate",
                    date?.format("YYYY-MM-DD")
                  );
                  setOpen(false);
                }}
                format="MMMM D, YYYY"
                minDate={dayjs()}
                slotProps={{
                  inputAdornment: {
                    position: "start",
                    onClick: () => setOpen((prev) => !prev),
                  },
                  textField: {
                    variant: "outlined",
                    error:
                      formik.touched.appointmentDate &&
                      Boolean(formik.errors.appointmentDate),
                    helperText:
                      formik.touched.appointmentDate &&
                      formik.errors.appointmentDate,
                    sx: {
                      width: "100%",
                    },
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setOpen((prev) => !prev)}>
                            <ArrowDropDownOutlinedIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  },
                }}
              />
              {isEdit && (
                <TextField
                  sx={{ mt: 2, width: "100%" }}
                  id="reason"
                  name="reason"
                  label="Reason for Rescheduling?"
                  multiline
                  rows={2}
                  variant="outlined"
                  value={formik.values.reason}
                  onChange={formik.handleChange}
                />
              )}
            </Box>
          </LocalizationProvider>
          {showErrorNotification && (
            <ErrorNotification
              show={showErrorNotification}
              onClose={() => setShowErrorNotification(false)}
              title={errorMessage}
            />
          )}
          {loading && <Loading />}
        </form>
      </DialogContent>
      <DialogActions>
        <Button sx={{ mr: 1 }} variant="outlined" onClick={closeDialog}>
          Cancel
        </Button>
        <Button
          sx={styles.save}
          variant="contained"
          onClick={() => formik.handleSubmit()}
        >
          {isEdit ? "Reschedule" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
